<template>
  <v-data-table-server
    :key="currentCategoryId"
    :headers="headers"
    :items="filteredListings"
    :items-length="totalListings"
    :loading="loading"
    v-model:page="pagination.page"
    v-model:items-per-page="pagination.perPage"
    v-model:sort-by="pagination.sortBy"
    item-key="id"
    density="comfortable"
    class="text-body-2 elevation-1 clickable-table"
    hover
    striped
    @update:page="getlistings"
    @update:items-per-page="
      () => {
        pagination.page = 1;
        getlistings();
      }
    "
    @update:sort-by="getlistings"
    @click:row="(_, rowProps) => viewListing(rowProps.item)"
  >
    <template v-slot:top>
      <v-toolbar flat class="bg-white px-4 py-2">
        <v-row align="center" justify="space-between" no-gutters class="mb-4">
          <!-- 1) Search field -->
          <v-col cols="12" sm="3" class="mt-3">
            <v-text-field
              v-model="search"
              label="Search listings..."
              append-inner-icon="mdi-magnify"
              clearable
              rounded="pill"
              variant="outlined"
              density="comfortable"
              hide-details
              @input="getlistings"
            />
          </v-col>

          <!-- 2) Status chips -->
          <v-col cols="12" sm="5" class="text-center">
            <v-chip-group
              v-model="statusFilter"
              row
              class="justify-center"
              active-class="white--text"
            >
              <v-chip
                label
                rounded="pill"
                class="mx-1"
                :color="statusFilter === '' ? 'primary' : ''"
                :outlined="statusFilter !== ''"
                elevation="2"
              >
                All
              </v-chip>
              <v-chip
                label
                rounded="pill"
                class="mx-1"
                :color="statusFilter === 1 ? 'success' : ''"
                :outlined="statusFilter !== 1"
                elevation="2"
              >
                <v-icon left small>mdi-check-circle-outline</v-icon>
                Approved
              </v-chip>
              <v-chip
                label
                rounded="pill"
                class="mx-1"
                :color="statusFilter === 2 ? 'warning' : ''"
                :outlined="statusFilter !== 2"
                elevation="2"
              >
                <v-icon left small>mdi-timer-sand</v-icon>
                Pending
              </v-chip>
              <v-chip
                label
                rounded="pill"
                class="mx-1"
                :color="statusFilter === 3 ? 'error' : ''"
                :outlined="statusFilter !== 3"
                elevation="2"
              >
                <v-icon left small>mdi-close-circle-outline</v-icon>
                Rejected
              </v-chip>
            </v-chip-group>
          </v-col>

          <!-- 3) Export / New Listing -->
          <v-col cols="12" sm="4" class="text-right">
            <v-btn
              color="primary"
              prepend-icon="mdi-download"
              class="mx-1"
              @click="exportToCSV"
            >
              Export CSV
            </v-btn>
            <!-- <v-btn
            color="black"
            prepend-icon="mdi-plus"
            variant="flat"
            class="ml-2"
            @click="dialog = true"
          >
            New Listing
          </v-btn> -->
          </v-col>
        </v-row>
      </v-toolbar>
    </template>

    <template v-slot:[`item.image`]="{ item }">
      <v-img
        :src="
          item.image
            ? `${API_URL}${item.image}`
            : 'https://via.placeholder.com/60x40?text=N/A'
        "
        width="60"
        height="40"
        class="rounded"
        cover
      />
    </template>

    <template v-slot:[`item.title`]="{ item }">
      <v-edit-dialog
        :return-value.sync="item.title"
        @save="updateInline(item, 'title')"
        @cancel="getlistings"
      >
        <span class="small-title">{{ item.title }}</span>
        <template v-slot:input>
          <v-text-field v-model="item.title" label="Edit Title" single-line />
        </template>
      </v-edit-dialog>
    </template>

    <template v-slot:[`item.status`]="{ item }">
      <v-chip
        :color="
          item.status === 1 ? 'success' : item.status === 3 ? 'error' : 'orange'
        "
        text-color="white"
        size="x-small"
        label
      >
        {{ getStatusTitle(item.status) }}
      </v-chip>
    </template>

    <template v-slot:[`item.verified`]="{ item }">
      <v-chip
        :color="
          item.verified === 1
            ? 'success'
            : item.status === 0
            ? 'error'
            : 'orange'
        "
        text-color="white"
        size="x-small"
        label
      >
        {{ getStatusVerified(item.verified) }}
      </v-chip>
    </template>

    <template v-slot:[`item.price`]="{ item }">
      <v-icon size="14" class="mr-1">mdi-currency-usd</v-icon>
      {{ convertToCurrency(item.price) }}
    </template>

    <template v-slot:[`item.actions`]="{ item }">
      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn
            icon="mdi-dots-vertical"
            size="x-small"
            variant="outlined"
            v-bind="props"
            color="black"
          />
        </template>
        <v-list>
          <v-list-item>
            <v-btn
              icon="mdi-eye"
              size="x-small"
              color="black"
              variant="outlined"
              @click="viewListing(item)"
            />
          </v-list-item>
          <v-list-item>
            <v-btn
              icon="mdi-delete"
              size="x-small"
              color="error"
              variant="outlined"
              @click="deleteItem(item)"
            />
          </v-list-item>
        </v-list>
      </v-menu>
    </template>

    <template v-slot:no-data>
      <div class="text-center pa-5">
        <v-icon size="36" color="grey">mdi-database-remove</v-icon>
        <div class="text-subtitle-2 mt-2">No listings available</div>
        <v-btn color="black" class="mt-2" @click="initialize">Reset</v-btn>
      </div>
    </template>
    <!-- Our Reusable Listing Detail Modal -->
  </v-data-table-server>

  <ListingDetailView
    v-model="detailDialog"
    :listing="selectedListing"
    :loading-approve="approving"
    :loading-verify="verifying"
    :loading-reject="rejecting"
    @approve="onApprove"
    @verify="onVerify"
    @reject="showRejectDialog = true"
  />

  <v-dialog v-model="dialogDelete" max-width="400px">
    <v-card>
      <v-card-title>
        <span class="text-h6">Delete Listing</span>
      </v-card-title>
      <v-card-text>
        Are you sure you want to <b>delete</b> this listing?
        <div class="mt-2">
          <b>{{ editedItem.title }}</b>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions class="justify-end">
        <v-btn text @click="closeDelete">Cancel</v-btn>
        <v-btn color="error" :loading="deleting" @click="deleteItemConfirm"
          >Delete</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- inside your <template>, probably just after ListingDetailModal -->
  <v-dialog v-model="showRejectDialog" max-width="400px">
    <v-card>
      <v-card-title>
        <span class="text-h6">Reason for Rejection</span>
      </v-card-title>
      <v-card-text>
        <v-textarea
          v-model="rejectReason"
          label="Please enter a reason"
          rows="3"
          auto-grow
          :rules="[(v) => !!v || 'Reason is required']"
        />
      </v-card-text>
      <v-divider />
      <v-card-actions class="justify-end">
        <v-btn text @click="() => (showRejectDialog = false)">Cancel</v-btn>
        <v-btn
          color="error"
          :loading="rejecting"
          :disabled="rejecting || !rejectReason"
          @click="confirmReject"
        >
          Reject
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, nextTick } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import moment from "moment";
import { API_URL } from "@/helpers/api.js";
import ListingService from "../services/listing.service.js";
import CategoriesService from "../services/category.services.js";
import CitiesService from "../services/cities.services.js";
import TransactionsService from "../services/transaction.service.js";
import UsersService from "../services/user.service.js";

// All component imports remain as before

import ListingDetailView from "@/components/Listings/ListingDetailView.vue";

const store = useStore();
const router = useRouter();
const route = useRoute();

const dialog = ref(false);
const dialogDelete = ref(false);
const ListDialog = ref(false);
const selectedItem = ref({});
const selectedListing = ref(null);
const detailDialog = ref(false);
const isValid = ref(false);
const rules = {
  name: [
    (value) => {
      if (value?.length >= 3) return true;
      return "You must enter a name.";
    },
  ],
  slug: [
    (value) => {
      if (value?.length >= 1) return true;
      return "You must enter a item.";
    },
  ],
  date: [
    (v) => !!v || "Expiry date is required",
    (v) => (v && moment(v, "MM-DD-YYYY", true).isValid()) || "Invalid date",
  ],
};
const statusFilter = ref("");
const search = ref("");
const pagination = reactive({
  page: 1,
  perPage: 10,
  sortBy: [{ key: "id", order: "asc" }],
});

// whenever page size changes, re-fetch
// watch(
//   () => pagination.perPage,
//   () => {
//     pagination.page = 1; // optional: back to first page
//     getlistings();
//   }
// );

// also re-fetch when user navigates pages or sorts:
// watch(() => pagination.page, getlistings);
// watch(() => pagination.sortBy, getlistings);
const approving = ref(false);
const rejecting = ref(false);
const verifying = ref(false);
const showRejectDialog = ref(false);
const rejectReason = ref("");
const API_URL_LOCAL = API_URL;

const headers = [
  {
    title: "#",
    value: "displayIndex",
    align: "start",
    sortable: false,
    key: "displayIndex",
  },
  {
    title: "Title",
    value: "title",
    key: "title",
    width: "700px",
    minWidth: "300px",
    cellClass: "small-title",
    headerClass: "small-title",
  },
  { title: "Price(usd)", value: "price", key: "price" },
  { title: "Verification", value: "verified", key: "verified" },
  { title: "Category", value: "category.title", key: "category.title" },
  {
    title: "Location(City)",
    value: "location.location_name",
    key: "location.id",
  },
  { title: "Status", value: "status", key: "status" },
  { title: "Approved Date", value: "ApprovedDate", key: "ApprovedDate" },
  { title: "Image", value: "image" },
  { title: "Actions", key: "actions", align: "end", sortable: false },
];

const editedIndex = ref(-1);
const categories = ref([]);
const transactions = ref([]);
const users = ref([]);
const cities = ref([]);
const newFiles = ref([]);
const loading = computed(() => store.getters["allListings/isLoading"]);
const fetchError = computed(() => store.getters["allListings/fetchError"]);

const statuses = [
  { id: 1, title: "Approved" },
  { id: 2, title: "Pending" },
  { id: 3, title: "Rejected" },
];
const statusVerification = [{ id: 1, title: "Verified" }];
const fetured = [
  { id: 1, title: "Featured" },
  { id: 0, title: "Not Featured" },
];

const editedItem = reactive({
  title: "",
  price: "",
  categoryId: "",
  transactionId: "",
  userId: "",
  cityId: "",
  isFeatured: "",
  view: "",
  tags: "",
  evc: "",
  currency: "",
  contactInformation: "",
  expires_at: new Date(),
  status: "",
  ApprovedDate: new Date(),
  rejectReason: "",
  description: "",
  condition: "",
  files: [],
});
const defaultItem = {
  title: "",
  price: "",
  categoryId: "",
  transactionId: "",
  userId: "",
  cityId: "",
  isFeatured: "",
  view: "",
  tags: "",
  evc: "",
  currency: "",
  contactInformation: "",
  expires_at: new Date(),
  status: "",
  approvedDate: new Date(),
  rejectReason: "",
  description: "",
  condition: "",
  files: [],
};

// Getters from store
const listings = computed(() => store.getters["allListings/allListings"]);
const totalListings = computed(
  () => store.getters["allListings/listingsTotal"]
);

const formTitle = computed(() =>
  editedIndex.value === -1 ? "New Listing" : "Edit Listing"
);
const loggedInUser = computed(() => store.state.auth.user);

const currentCategoryId = computed(() => {
  const routeToCategory = {
    ListingsMotors: 1,
    ListingsJobs: 2,
    ListingsPropertyForSale: 3,
    ListingsPropertyForRent: 4,
    ListingsCommunity: 5,
    ListingsClassifieds: 6,
    ListingsFunitureGarden: 7,
    ListingsMobileTablets: 8,
  };
  return routeToCategory[route.name] ?? null;
});

// const filteredListings = computed(() => {
//   let filtered = [...listings.value];
//   if (route.name !== "Listings" && currentCategoryId.value) {
//     filtered = filtered.filter(
//       (item) => item.category && item.category.id === currentCategoryId.value
//     );
//   }
//   const start = (pagination.page - 1) * pagination.perPage;
//   return filtered.map((item, index) => ({
//     ...item,
//     displayIndex: start + index + 1,
//   }));
// });

const filteredListings = computed(() => {
  const start = (pagination.page - 1) * pagination.perPage;
  return listings.value.map((item, i) => ({
    ...item,
    displayIndex: start + i + 1,
  }));
});

const formattedExpiryDate = computed({
  get() {
    return editedItem.expiryDate
      ? moment(editedItem.expiryDate).format("YYYY-MM-DD")
      : "";
  },
  set(value) {
    editedItem.expiryDate = moment(value, "YYYY-MM-DD").toDate();
  },
});

const formattedApprovedDate = computed({
  get() {
    return editedItem.ApprovedDate
      ? moment(editedItem.ApprovedDate).format("YYYY-MM-DD")
      : "";
  },
  set(value) {
    editedItem.ApprovedDate = moment(value, "YYYY-MM-DD").toDate();
  },
});

const formattedStartDate = computed({
  get() {
    return editedItem.startDate
      ? moment(editedItem.startDate).format("YYYY-MM-DD")
      : "";
  },
  set(value) {
    editedItem.startDate = moment(value, "YYYY-MM-DD").toDate();
  },
});

// Utility functions (must be defined for use in template!)
function convertToCurrency(value) {
  if (value === null || value === undefined) return "";
  let number = value;
  if (typeof value === "string") {
    number = parseFloat(value.replace(/[^0-9.-]+/g, ""));
  }
  return !isNaN(number) ? number.toLocaleString("en-US") : "Invalid number";
}
function getStatusTitle(statusId) {
  const status = statuses.find((status) => status.id === statusId);
  return status ? status.title : "Unknown";
}
function getStatusVerified(statusId) {
  const status = statusVerification.find((status) => status.id === statusId);
  return status ? status.title : "Unverified";
}
function formatDate(date) {
  return moment(date).format("MM-DD-YYYY");
}
function formatPhoneNumber(number) {
  number = number.replace(/[^\d+]/g, "");
  if (number.startsWith("0")) {
    return "+252" + number.slice(1);
  }
  if (number.startsWith("252")) {
    return "+" + number;
  }
  if (number.startsWith("+252")) {
    return number;
  }
  return "+252" + number;
}
function getCategoryComponent(slug) {
  switch (slug) {
    case "property":
      return "PropertyList";
    case "motors":
      return "MotorList";
    case "community":
      return "CommunityList";
    case "jobs":
      return "JobList";
    case "classified":
      return "Classified";
    case "funiture&garden":
      return "FurnitureGarden";
    case "mobile&tables":
      return "MobileTablet";
    default:
      return null;
  }
}

function addFiles() {
  editedItem.files = [...newFiles.value];
}

// Main CRUD and UI flow methods
function getlistings() {
  const params = {
    page: pagination.page,
    perPage: pagination.perPage,
    sortBy: pagination.sortBy[0]?.key || "id",
    sortDesc: pagination.sortBy[0]?.order === "desc",
    search: search.value || "",
    status: statusFilter.value || "",
    category_id: currentCategoryId.value || "", // <— add this
    only_reported: false,
    all: false,
  };
  store.dispatch("allListings/fetchAllListings", params);
}

function exportToCSV() {
  if (!listings.value.length) return;
  const csvContent = [
    Object.keys(listings.value[0]).join(","), // headers
    ...listings.value.map((item) =>
      Object.values(item)
        .map((v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`)
        .join(",")
    ),
  ].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", "listings_export.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
function handleLogout() {
  store.commit("logout");
  store.dispatch("resetInactivityTimeout");
  store.dispatch("auth/logout");
  store.dispatch("resetInactivityTimeout");
  router.push("/login");
  store.dispatch("alert/success", "Logout Successfully");
}
function getCategories() {
  return CategoriesService.onlyCategories().then(
    (response) => {
      if (response.status == 200) {
        categories.value = response.data.data.map((category) => ({
          ...category,
          title: category.name,
        }));
      } else {
        categories.value = [];
      }
    },
    (error) => {
      categories.value = [];
      if (error.response.status == 401) {
        handleLogout();
      }
      store.dispatch("alert/error", error.message);
    }
  );
}
function closeDialog() {
  ListDialog.value = false;
  getlistings();
}
function gettransactions() {
  return TransactionsService.index().then(
    (response) => {
      if (response.status == 200) {
        transactions.value = response.data.data.map((transaction) => ({
          ...transaction,
          title: transaction.transactionReference,
        }));
      } else {
        transactions.value = [];
      }
    },
    (error) => {
      transactions.value = [];
      if (error.response.status == 401) {
        handleLogout();
      }
      store.dispatch("alert/error", error.message);
    }
  );
}
function getUsers() {
  return UsersService.index().then(
    (response) => {
      if (response.status == 200) {
        users.value = response.data.data.map((user) => ({
          ...user,
          title: user.fullName,
        }));
      } else {
        users.value = [];
      }
    },
    (error) => {
      users.value = [];
      if (error.response.status == 401) {
        handleLogout();
      }
      store.dispatch("alert/error", error.message);
    }
  );
}
function getCities() {
  return CitiesService.index().then(
    (response) => {
      if (response.status == 200) {
        cities.value = response.data.data;
      } else {
        cities.value = [];
      }
    },
    (error) => {
      cities.value = [];
      if (error.response.status == 401) {
        handleLogout();
      }
      store.dispatch("alert/error", error.message);
    }
  );
}
function setSelectedItem(item) {
  store.dispatch("category/setSelectedItem", item);
  selectedItem.value = { ...item };
  ListDialog.value = true;
}
function editItem(item) {
  editedIndex.value = listings.value.indexOf(item);
  Object.assign(editedItem, {
    id: item.id,
    title: item.title || "",
    price: item.price || 0,
    categoryId: item.category ? item.category : "",
    transactionId: item.transactionId || "",
    userId: item.user ? item.user : "",
    cityId: item.city ? item.city : "",
    isFeatured: item.isFeatured || "",
    views: item.views || "",
    tags: item.tags || "",
    evc: item.evc || "",
    currency: item.currency || "",
    contactInformation: item.contactInformation || "",
    expires_at: item.expires_at ? new Date(item.expires_at) : new Date(),
    status: item.status || "",
    approvedDate: item.approvedDate
      ? formatDate(item.ApprovedDate)
      : formatDate(moment()),
    rejectReason: item.rejectReason || "",
    description: item.description || "",
    condition: item.condition || "",
    files: item.files || [],
  });
  if (!editedItem.ApprovedDate) {
    editedItem.ApprovedDate = formatDate(moment());
  }
  dialog.value = true;
}
function deleteItem(item) {
  editedIndex.value = listings.value.indexOf(item);
  Object.assign(editedItem, item);
  dialogDelete.value = true;
}

const deleting = ref(false);

async function deleteItemConfirm() {
  deleting.value = true;
  try {
    // Use the Vuex action instead of the service directly
    await store.dispatch("allListings/deleteListing", editedItem);
    store.dispatch("alert/success", "Listing deleted successfully");
    closeDelete();
    // No need to manually call getlistings(); store handles refresh
  } catch (err) {
    store.dispatch(
      "alert/error",
      err.response?.data?.message || "Delete failed"
    );
  } finally {
    deleting.value = false;
  }
}

function closeDelete() {
  dialogDelete.value = false;
  nextTick(() => {
    Object.assign(editedItem, defaultItem);
    editedIndex.value = -1;
  });
}

function close() {
  dialog.value = false;
  nextTick(() => {
    Object.assign(editedItem, defaultItem);
    editedIndex.value = -1;
  });
}
// function closeDelete() {
//   dialogDelete.value = false;
//   nextTick(() => {
//     Object.assign(editedItem, defaultItem);
//     editedIndex.value = -1;
//   });
// }
function save() {
  editedItem.name = editedItem.title;
  if (validate()) {
    if (editedIndex.value > -1) {
      Object.assign(listings.value[editedIndex.value], editedItem);
      ListingService.update(editedItem).then(
        (response) => {
          if (response.status == 200) {
            store.dispatch("alert/success", response.data.message);
            close();
          } else {
            store.dispatch("alert/error", response.data.message);
          }
        },
        (error) => {
          if (error.response.status == 422) {
            store.dispatch("alert/error", error.response.data.message);
          }
          store.dispatch("alert/error", error.response.data.message);
        }
      );
    } else {
      ListingService.store(editedItem).then(
        (response) => {
          if (response.status == 201) {
            store.dispatch("alert/success", response.data.message);
            close();
            getlistings();
          } else {
            store.dispatch("alert/error", response.data.message);
          }
        },
        (error) => {
          if (error.response.status == 422) {
            store.dispatch("alert/error", error.response.data.message);
          }
          store.dispatch("alert/error", error.response.data.message);
        }
      );
    }
  }
}
function updateInline(item, field) {
  ListingService.update(item)
    .then(() => {
      store.dispatch("alert/success", "Updated successfully");
      getlistings(); // re-fetch after inline update
    })
    .catch((err) => {
      store.dispatch(
        "alert/error",
        err.response?.data?.message || "Failed to update"
      );
      getlistings();
    });
}

function viewListing(listing) {
  selectedListing.value = listing;
  detailDialog.value = true;
}

async function onApprove(listing) {
  approving.value = true;
  try {
    await ListingService.approve(listing.id);
    store.dispatch("alert/success", "Listing approved successfully");
    detailDialog.value = false;
    getlistings();
  } catch (err) {
    store.dispatch(
      "alert/error",
      err.response?.data?.message || "Failed to approve"
    );
  } finally {
    approving.value = false;
  }
}
async function onVerify(listing) {
  verifying.value = true;
  try {
    await ListingService.verify(listing.id);
    store.dispatch("alert/success", "Listing verified successfully");
    detailDialog.value = false;
    getlistings();
  } catch (err) {
    store.dispatch(
      "alert/error",
      err.response?.data?.message || "Failed to verify"
    );
  } finally {
    verifying.value = false;
  }
}
async function confirmReject() {
  rejecting.value = true;
  try {
    await ListingService.decline(selectedListing.value.id, {
      rejectReason: rejectReason.value,
    });
    store.dispatch("alert/success", "Listing declined successfully");
    showRejectDialog.value = false;
    detailDialog.value = false;
    getlistings();
  } catch (err) {
    store.dispatch(
      "alert/error",
      err.response?.data?.message || "Failed to decline"
    );
  } finally {
    rejecting.value = false;
    rejectReason.value = "";
  }
}
function validate() {
  // You can adapt this to use any validation mechanism you use in setup API
  // For demo, always returns true.
  return true;
}

// Watches and lifecycle
watch(dialog, (val) => {
  if (!val) close();
});
watch(dialogDelete, (val) => {
  if (!val) closeDelete();
});

watch(currentCategoryId, () => {
  pagination.page = 1;
  getlistings();
});

// also reset to page 1 on filter changes
watch(statusFilter, () => {
  pagination.page = 1;
  getlistings();
});

onMounted(() => {
  getCategories();
  gettransactions();
  getUsers();
  getCities();
  getlistings(); // initial fetch
});
</script>

<style scoped>
.fixed-bar {
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  z-index: 2;
}

.drag-handle {
  cursor: move;
}

.v-data-table >>> td:nth-child(2),
.v-data-table >>> th:nth-child(2) {
  min-width: 250px !important;
  word-wrap: break-word;
  white-space: normal;
}

.small-title {
  font-family: Roboto, sans-serif !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 19.95px !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.clickable-table .v-data-table__tbody tr {
  cursor: pointer;
  transition: background 0.1s;
}
.clickable-table .v-data-table__tbody tr:hover {
  background: #f5f7fa !important;
}
</style>
